import React, { useEffect, useRef } from 'react'

import { highlightSpecialChars } from '@codemirror/view'
import { langs } from '@uiw/codemirror-extensions-langs'
import { githubLight } from '@uiw/codemirror-theme-github'
import CodeMirror, { ReactCodeMirrorRef } from '@uiw/react-codemirror'
import { EditorView } from 'codemirror'
import { useLocation } from 'react-router-dom'

import style from './Editor.module.css'

type PropsType = {
  language: string
  editable: boolean
  extraExtensions: any[]
  value?: string
  onChange?: (value: string, view: any) => void
  onCreateEditor?: (view: EditorView, state: any) => void
  scrollTop?: number
}

const getExtensions = (language: string) => {
  switch (language) {
    case 'js':
      return langs.javascript()
    case 'css':
      return langs.css()
    case 'jsx':
      return langs.jsx()
    case 'ts':
      return langs.typescript()
    case 'sql':
      return langs.sql()
    default:
      return langs.markdown()
  }
}

const specialCharsHighlight = (): HTMLElement => {
  const element = document.createElement('span')

  element.textContent = '•'
  element.classList.add('cm-specialChar')
  element.contentEditable = 'false'
  element.title = 'Trailing space'

  return element
}

export const Editor = ({
  value = '',
  language,
  editable,
  onChange,
  extraExtensions,
  onCreateEditor,
  scrollTop,
}: PropsType) => {
  const location = useLocation()

  const regexp = language === 'html' ? / (?= *$)/g : / /g

  const ref = useRef<ReactCodeMirrorRef>(null)
  const extensions = [
    getExtensions(language),
    EditorView.lineWrapping,
    highlightSpecialChars({
      specialChars: regexp,
      render: specialCharsHighlight,
    }),
    ...extraExtensions,
  ]

  // autofocus and text reset
  useEffect(() => {
    if (editable) {
      ref.current?.view?.focus()
      ref.current!.view?.dispatch({
        changes: {
          from: 0,
          to: ref.current!.view?.state.doc.length,
          insert: '',
        },
      })
    }
  }, [location])

  // scroll synchronization
  useEffect(() => {
    if (!editable) {
      ref.current?.editor?.querySelector('.cm-scroller')?.scrollTo(0, scrollTop!)
    }
  }, [scrollTop])

  return (
    <CodeMirror
      ref={ref}
      autoFocus
      className={`${style.editor} ${editable ? '' : 'task-code'}`}
      value={value}
      extensions={extensions}
      onChange={onChange}
      onCreateEditor={onCreateEditor}
      editable={editable}
      theme={githubLight}
      basicSetup={{
        foldGutter: false,
        autocompletion: false,
        highlightActiveLineGutter: editable,
        highlightActiveLine: editable,
      }}
    />
  )
}
